/**
 * Image optimization utilities for better performance
 * Handles responsive images, modern formats, and proper sizing
 */

export interface ImageSize {
  width: number;
  height?: number;
}

export interface OptimizedImageUrl {
  src: string;
  srcSet?: string;
  sizes?: string;
  width?: number;
  height?: number;
}

/**
 * Check if browser supports modern image formats
 */
export const supportsWebP = (): boolean => {
  if (typeof window === 'undefined') return false;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

export const supportsAVIF = (): boolean => {
  if (typeof window === 'undefined') return false;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
};

/**
 * Get optimal image width based on display size and device pixel ratio
 */
export const getOptimalImageWidth = (
  displayWidth: number,
  devicePixelRatio: number = 1
): number => {
  // Round up to nearest 100 for better caching
  return Math.ceil(displayWidth * Math.min(devicePixelRatio, 2) / 100) * 100;
};

/**
 * Check if the image server supports URL transformations
 */
const supportsImageTransform = (url: string): boolean => {
  // Mobiauto image API supports transforms
  if (url.includes('image1.mobiauto.com.br') || url.includes('image.mobiauto.com.br')) {
    return true;
  }
  
  // Other CDNs that might support transforms (add as needed)
  // Cloudinary, Imgix, etc.
  
  // Azure Blob Storage, S3, and other static storage don't support transforms
  return false;
};

/**
 * Generate responsive image URLs for mobiauto image API
 * API format: https://image1.mobiauto.com.br/images/api/images/v1.0/{id}/transform/w_{width}
 * 
 * For other servers (Azure Blob, S3, etc.), returns original URL with proper attributes
 */
export const generateResponsiveImageUrls = (
  baseUrl: string,
  displayWidth: number,
  displayHeight?: number
): OptimizedImageUrl => {
  try {
    // Check if server supports image transformations
    if (!supportsImageTransform(baseUrl)) {
      // For servers that don't support transforms (Azure Blob, S3, etc.)
      // Return original URL but still provide width/height hints for browser optimization
      const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
      const optimalWidth = getOptimalImageWidth(displayWidth, devicePixelRatio);
      
      return {
        src: baseUrl,
        // No srcset for non-transformable images
        sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 260px',
        width: optimalWidth,
        height: displayHeight ? Math.round(optimalWidth / (displayWidth / displayHeight)) : undefined
      };
    }

    // Server supports transforms - proceed with optimization
    const urlObj = new URL(baseUrl);
    const pathParts = urlObj.pathname.split('/').filter(p => p);
    
    // Find the transform index
    const transformIndex = pathParts.findIndex(part => part === 'transform');
    
    let baseImageUrl: string;
    
    if (transformIndex === -1) {
      // No transform found - try to find image ID pattern
      // Pattern: images/api/images/v1.0/{id}
      const v10Index = pathParts.findIndex((part, idx) => 
        idx > 0 && pathParts[idx - 1] === 'v1.0' && /^\d+$/.test(part)
      );
      
      if (v10Index !== -1) {
        const basePath = '/' + pathParts.slice(0, v10Index + 1).join('/');
        baseImageUrl = `${urlObj.origin}${basePath}`;
      } else {
        // Can't parse mobiauto URL, return original
        return { src: baseUrl };
      }
    } else {
      // Transform found - get base path before transform
      const basePath = '/' + pathParts.slice(0, transformIndex).join('/');
      baseImageUrl = `${urlObj.origin}${basePath}`;
    }

    // Calculate optimal widths for srcset
    const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const optimalWidth = getOptimalImageWidth(displayWidth, devicePixelRatio);
    
    // Generate srcset with different sizes (smaller breakpoints for cards)
    const breakpoints = [300, 400, 600, 800];
    const srcSetParts: string[] = [];
    
    // Add sizes for different viewport widths
    const sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 260px';

    // Generate srcset entries
    breakpoints.forEach(width => {
      if (width <= optimalWidth * 2) { // Include sizes up to 2x the optimal
        const transformUrl = `${baseImageUrl}/transform/w_${width}`;
        srcSetParts.push(`${transformUrl} ${width}w`);
      }
    });
    
    // Add optimal width if not in breakpoints
    if (!breakpoints.includes(optimalWidth) && optimalWidth <= 1200) {
      const transformUrl = `${baseImageUrl}/transform/w_${optimalWidth}`;
      srcSetParts.push(`${transformUrl} ${optimalWidth}w`);
    }

    // Main src (use optimal width)
    let finalSrc = `${baseImageUrl}/transform/w_${optimalWidth}`;
    
    // Add height if provided for better aspect ratio
    if (displayHeight) {
      const aspectRatio = displayWidth / displayHeight;
      const optimalHeight = Math.round(optimalWidth / aspectRatio);
      finalSrc = `${baseImageUrl}/transform/w_${optimalWidth},h_${optimalHeight}`;
    }

    return {
      src: finalSrc,
      srcSet: srcSetParts.length > 0 ? srcSetParts.join(', ') : undefined,
      sizes: sizes,
      width: optimalWidth,
      height: displayHeight ? Math.round(optimalWidth / (displayWidth / displayHeight)) : undefined
    };
  } catch (error) {
    // If URL parsing fails, return original URL
    console.warn('Failed to optimize image URL:', baseUrl, error);
    return { src: baseUrl };
  }
};

/**
 * Generate optimized image URL with modern format support
 * Tries WebP/AVIF first, falls back to original format
 */
export const getOptimizedImageUrl = (
  baseUrl: string,
  width: number,
  height?: number,
  format?: 'webp' | 'avif' | 'auto'
): string => {
  // Only apply transforms to servers that support it
  if (!supportsImageTransform(baseUrl)) {
    return baseUrl;
  }

  // Parse the URL
  const urlObj = new URL(baseUrl);
  const pathParts = urlObj.pathname.split('/');
  
  // Find transform or add it
  const transformIndex = pathParts.findIndex(part => part === 'transform');
  let basePath: string;
  
  if (transformIndex === -1) {
    basePath = pathParts.join('/');
  } else {
    basePath = pathParts.slice(0, transformIndex).join('/');
  }

  // Build transform parameters
  let transformParams = `w_${width}`;
  if (height) {
    transformParams += `,h_${height}`;
  }

  // Add format if supported and requested
  if (format === 'auto') {
    if (supportsAVIF()) {
      transformParams += ',f_avif';
    } else if (supportsWebP()) {
      transformParams += ',f_webp';
    }
  } else if (format) {
    transformParams += `,f_${format}`;
  }

  return `${urlObj.origin}${basePath}/transform/${transformParams}`;
};

/**
 * Get appropriate image loading strategy
 */
export const getImageLoadingStrategy = (
  isAboveFold: boolean,
  isLCP: boolean = false
): { loading: 'lazy' | 'eager'; fetchpriority: 'high' | 'low' | 'auto' } => {
  if (isLCP || isAboveFold) {
    return { loading: 'eager', fetchpriority: 'high' };
  }
  return { loading: 'lazy', fetchpriority: 'auto' };
};

/**
 * Calculate display dimensions from CSS classes or container
 */
export const getDisplayDimensions = (
  containerWidth?: number,
  aspectRatio?: number
): { width: number; height?: number } => {
  // Default card dimensions
  const defaultCardWidth = 260; // Based on VehicleCard w-[260px]
  const defaultAspectRatio = 4 / 2.5; // aspect-[4/2.5]

  const width = containerWidth || defaultCardWidth;
  const height = aspectRatio ? Math.round(width / aspectRatio) : undefined;

  return { width, height };
};

